import store from "@/js/store";
import request from "@/js/request";
import InputPill from "@/js/input-pill.js";
import Typeahead from "@/js/typeahead.js";

import { LOCALES, LOCALE_CODES } from "@/js/locales.js";

const internal = {
  padNumber: (n, length) => ("0".repeat(length - 1) + n).slice(-length),
  sort: {
    byCreatedAt: o => o.sort((a, b) => {
      if (a.createdAt < b.createdAt) return 1;
      else if (a.createdAt === b.createdAt) return 0;
      return -1;
    }),
  },

  // this is a method for adding functions to a callback queue to be run once
  // a particular event has happened.
  // the way to use this essentially is by creating a Queue instance and then
  // adding each of the functions individually, and then running the `run` method.
  // for functions with arguments, pass them in in an empty object rather than
  // using something like `.apply` or `.call`. There may be side effects with
  // the latter.
  Queue: () => {
    const cbs = [];
    let done = false;
    let variable = null;

    let obj = {
      add: (cb) => {
        if (done) cb(variable);
        else cbs.push(cb);
      },
      run: (v) => {
        done = true;
        variable = v;
        cbs.forEach(cb => cb(variable));
      },
    };

    return obj;
  },

  noDoubleRequest: (req_func) => {
    let outstanding_request = false;
    let queue = internal.Queue();

    return () => {
      if (outstanding_request === false) {
        outstanding_request = req_func;
        outstanding_request().then((result) => {
          queue.run(result);
          outstanding_request = false;
          queue = internal.Queue();
        });
      }

      // this is quite a thing -- but basically the argument #1 for Promise
      // is resolve, and the queue.add argument #1 is a callback, so it doesn't
      // need explicit calling/wrapping.
      return new Promise(queue.add);
    };
  },
};

const proto = {
  fetch: {
    statistics: () => request(`${proto.api_path.pay}/organizations/me/statistics/`, {
      credentials: true,
    }).get(),

    me: internal.noDoubleRequest(() => request(`${proto.api_path.pay}/organizations/me/`, {
      credentials: true,
    }).get()),

    testing_organization: internal.noDoubleRequest(() => request(`${proto.api_path.test}/organizations/me/`, {
      headers: { Authorization: `Basic ${store.organization.testing_organization_auth_token}` },
    }).get()),

    testing: {
      strategies: internal.noDoubleRequest(() => request(`${proto.api_path.test}/organizations/me/strategies/`, {
        headers: { Authorization: `Basic ${store.organization.testing_organization_auth_token}` },
      }).get()),
    },

    sites: () => request(`${proto.api_path.pay}/organizations/me/sites/`, {
      credentials: true,
    }).get(),

    articles: site_public_key => request(`${proto.api_path.pay}/organizations/me/sites/${site_public_key}/articles/`, {
      headers: { Authorization: `Basic ${store.organization.auth_token}` },
    }).get(),

    subscriptions: site_public_key => request(`${proto.api_path.pay}/organizations/me/sites/${site_public_key}/subscriptions/`, {
      headers: { Authorization: `Basic ${store.organization.auth_token}` },
    }).get(),
  },

  waterfall: () => {
    let cbs = 0,
        cb,
        finished = 0;

    return {
      add: async (callback) => {
        cbs++;
        await callback();
        if (++finished === cbs) cb();
      },
      done: (callback) => {
        cb = callback;
      },
    };
  },

  sortBy: (arr, attr) => {
    return arr.sort((a, b) => {
      if (a[attr] > b[attr]) return -1;
      else if (a[attr] === b[attr]) return 0;
      return 1;
    });
  },

  // NOTE: this below uses some ordered async operations and some parallel async
  // operations. this is because
  generalInformation: async (payload) => {
    if (!payload) payload = {};
    if (!payload.update) payload.update = [];

    let waterfall = proto.waterfall();

    if (Object.keys(store.statistics).length === 0) {
      Object.assign(store.statistics, await proto.fetch.statistics());
    }

    if (Object.keys(store.organization).length === 0) {
      Object.assign(store.organization, await proto.fetch.me());
    }

    waterfall.add(async () => {
      if (store.sites.length === 0) {
        store.sites.push.apply(store.sites, await proto.fetch.sites());
      } else if (payload.update.indexOf("sites") !== -1) {
        store.sites.splice(0, store.sites.length);
        store.sites.push.apply(store.sites, await proto.fetch.sites());
      }

      internal.sort.byCreatedAt(store.sites);
    });

    waterfall.add(async () => {
      if (Object.keys(store.testing_organization).length === 0) {
        Object.assign(store.testing_organization, await proto.fetch.testing_organization());
        Object.assign(store.testing.strategies, internal.sort.byCreatedAt(await proto.fetch.testing.strategies()));
      } else {
        if (payload.update.indexOf("testing_organization") !== -1) {
          Object.assign(store.testing_organization, await proto.fetch.testing_organization());
        }

        if (payload.update.indexOf("strategies") !== -1) {
          Object.assign(store.testing.strategies, internal.sort.byCreatedAt(await proto.fetch.testing.strategies()));
        }
      }
    });

    if (payload && payload.site_public_key) {
      waterfall.add(async () => {
        if (!store.articles[payload.site_public_key] || payload.update.indexOf("articles") !== -1) {
          store.articles[payload.site_public_key] = internal.sort.byCreatedAt(await proto.fetch.articles(payload.site_public_key));
        }
      });

      waterfall.add(async () => {
        if (!store.subscriptions[payload.site_public_key] || payload.update.indexOf("subscriptions") !== -1) {
          store.subscriptions[payload.site_public_key] = await proto.fetch.subscriptions(payload.site_public_key);
        }
      });
    }

    return new Promise((resolve) => {
      waterfall.done(() => resolve(store));
    });
  },

  formatTest: {
    localeListToString: (() => {
      return (list, max_size) => {
        if (!list || list.length === 0) return `All regions`;

        list = list.map(o => o.toUpperCase());

        if (list.length === 1) {
          return LOCALES[list[0]];
        }

        if (list.length === 2) {
          return `${LOCALES[list[0]]} and ${LOCALES[list[1]]}`;
        }

        if (list.length > 2) {
          let str = list.slice(0, -1).map(o => LOCALES[o]).join(", ") + `, and ${LOCALES[list.slice(-1)[0]]}.`;

          if (max_size && str.length > max_size) {
            let idx = 0;
            str = ``;

            while ((str.length + (LOCALES[list[idx]] + ", ").length) < (max_size - "and x more.".length)) {
              str += LOCALES[list[idx++]] + ", ";
            }

            str += `and ${list.length - idx} more.`;

            return str;
          } else return str;
        }
        return list;
      }
    })(),
  },

  localeToCode: (locale) => {
    return LOCALE_CODES[locale];
  },

  formatTime: (date) => {
    let m = internal.padNumber(date.getMonth() + 1, 2),
        d = internal.padNumber(date.getDate(), 2),
        y = internal.padNumber(date.getFullYear(), 2);

    return `${m}/${d}/${y}`;
  },

  formatLengthOfTime: (ms) => {
    let minute = 1000 * 60,
        hour = minute * 60,
        day = hour * 24;

    if (ms < minute) return `${Math.round(ms / 1000)} seconds`;
    if (ms < hour) return `${Math.round(ms / minute)} minutes`;
    if (ms < day) return `${Math.round(ms / hour)} hours`;
    else return `${Math.round(ms / day)} days`;
  },

  lengthOfTimeToMilliseconds: (str) => {
    str = str.toLowerCase();

    if (/minute/.test(str)) return parseFloat(str) * 1000 * 60;
    if (/hour/.test(str)) return parseFloat(str) * 1000 * 60 * 60;
    if (/day/.test(str)) return parseFloat(str) * 1000 * 60 * 60 * 24;

    console.warn(`Error, the string "${str}" could not be converted. Please use the format "n [minutes/hours/days]"`);
  },

  getFormInput: (candidates) => {
    const obj = {};

    if (candidates instanceof Node) {
      candidates = candidates.querySelectorAll("[name]");
    }

    candidates.forEach((o) => {
      const name = o.getAttribute("name");

      if (o.tagName === "INPUT" || o.tagName === "TEXTAREA") {
        if (o.getAttribute("type") === "checkbox") {
          if (o.checked === true) {
            obj[name] = o.parentNode.querySelector(".form-switch input").checked;
          } else {
            obj[name] = null;
          }
        } else {
          obj[name] = o.value;

          if (o.getAttribute("type") === "number") {
            obj[name] = parseFloat(obj[name]);
          }
        }
      } else if (o.contentEditable === "true") {
        if (o.pills) obj[name] = o.pills.values();
        else obj[name] = o.innerText.trim();
      }
    });

    return obj;
  },


  api_path: {
    get pay () {
      return /localhost/.test(window.location.origin) ? `http://localhost:3000/api/v1` : `https://porticopay.com/api/v1`;
    },

    get test () {
      return /localhost/.test(window.location.origin) ? `http://localhost:3001/api/v1` : `https://testing.porticopay.com/api/v1`;
    },
  },

  nameToLabel: (name) => {
    return name
      .toLocaleLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^A-Za-z0-9-]/g, "");
  },

  createInputPillTypeaheadPair: ($el, payload) => {
    // state input pills
    var inputPill = InputPill($el.input_pills);

    inputPill.validate((val, _, reject) => {
      if (validDataToChoose().indexOf(val) === -1) {
        reject();
      }
    });

    inputPill.success(($parent) => $parent.addClass("full"));
    inputPill.onPillRemove(() => {
      if (inputPill.data().length === 0) $(".pill-container").removeClass("full")
    });

    let validDataToChoose = () => {
      let selected = inputPill.values();
      return payload.data.filter(state => selected.indexOf(state) === -1);
    };

    let typeahead = new Typeahead($el.typeahead, $el.typeahead_input);
    typeahead.data(validDataToChoose).on("submit", (value) => {
      inputPill.pill.append(value);
      inputPill.clearInput();
      typeahead.close();
    });

    return inputPill;
  },

  subscription: {
    labelToDuration: (d) => {
      // split something like "1 mo" into `["1", "mo"]`.
      d = d.split(/\s+/);

      let unit, value;

      if (/^d/.test(d[1])) unit = "day";
      else if (/^w/.test(d[1])) unit = "week";
      else if (/^m/.test(d[1])) unit = "month";
      else if (/^y/.test(d[1])) unit = "year";
      else return;

      if (+d[0]) value = +d[0];
      else return;

      return { unit, value };
    },

    durationToLabel: (duration) => {
      const { value, unit } = duration;
      let s = duration.value > 1 ? `s` : ``;

      return `${value} ${unit}${s}`;
    },
  }

};

export default proto;



// WEBPACK FOOTER //
// ./src/js/utils.js